import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';

interface CarouselProps {
  items: React.ReactNode[];
  itemsPerSlide?: number;
  showControls?: boolean;
  showDots?: boolean;
  className?: string;
  gap?: number;
  isMobile?: boolean;
}

const Carousel: React.FC<CarouselProps> = ({
  items,
  itemsPerSlide = 3,
  showControls = true,
  showDots = true,
  className = '',
  gap = 16,
  isMobile = false,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );

  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onInit, onSelect]);

  // Calcular a largura de cada slide baseado em itemsPerSlide
  const slideWidth = `calc((100% - ${gap * (itemsPerSlide - 1)}px) / ${itemsPerSlide})`;

  return (
    <div
      className={`relative rounded-[32px] bg-mono-color-lighter p-4 ${className}`}
    >
      <div className='overflow-hidden' ref={emblaRef}>
        <div
          className='flex'
          style={{
            gap: `${gap}px`,
            ...(!isMobile && {
              marginLeft: 40,
            }),
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className='bg-transparent min-w-0'
              style={{
                flex: `0 0 ${slideWidth}`,
                ...(isMobile && {
                  display: `flex`,
                  flexDirection: `column`,
                  alignItems: `center`,
                  justifyContent: `center`,
                }),
              }}
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Controls */}
      {showControls && (
        <>
          <button
            className='rounded-full bg-white shadow-lg absolute left-6 top-1/2 z-10 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center transition-all hover:scale-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100'
            onClick={scrollPrev}
            disabled={prevBtnDisabled}
            aria-label='Previous slide'
          >
            <svg
              className='h-8 w-8 text-brand-color-primary-pure'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 19l-7-7 7-7'
              />
            </svg>
          </button>

          <button
            className='bg-white shadow-lg absolute right-6 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-circular transition-all hover:scale-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100'
            onClick={scrollNext}
            disabled={nextBtnDisabled}
            aria-label='Next slide'
          >
            <svg
              className='h-8 w-8 text-brand-color-primary-pure'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 5l7 7-7 7'
              />
            </svg>
          </button>
        </>
      )}

      {/* Dots */}
      {showDots && scrollSnaps.length > 1 && (
        <div className='mt-4 flex items-center justify-center gap-2'>
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`h-2 w-2 rounded-circular transition-all ${
                index === selectedIndex
                  ? 'bg-color-brand-primary'
                  : 'bg-brand-color-primary-lighter hover:bg-color-brand-primary'
              }`}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
